import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../contexts/ToastContext';
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Save,
  FileText,
  User,
  Lock,
} from 'lucide-react';

function fleetUserFromSessionProfile(profile, prevUser) {
  if (!profile) return prevUser;
  const f = profile.fleet;
  return {
    id: profile.id,
    email: profile.email,
    first_name: profile.first_name,
    last_name: profile.last_name,
    phone: profile.phone,
    role: profile.role,
    status: profile.status,
    fleet: f
      ? {
          id: f.id,
          name: f.name,
          is_active: f.is_active,
        }
      : prevUser?.fleet ?? null,
  };
}

export default function Settings() {
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const [accountForm, setAccountForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    preferred_language: 'en',
  });
  const [fleetForm, setFleetForm] = useState({
    name: '',
    business_registration_number: '',
    address: '',
    phone: '',
    email: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const { data: profileData } = useQuery({
    queryKey: ['fleet-profile'],
    queryFn: async () => {
      try {
        return await authService.getProfile();
      } catch (error) {
        return { profile: { fleet: user?.fleet } };
      }
    },
  });

  useEffect(() => {
    if (!profileData) return;
    const p = profileData?.profile || {};
    setAccountForm({
      first_name: p.first_name || '',
      last_name: p.last_name || '',
      phone: p.phone || '',
      preferred_language: p.preferred_language || 'en',
    });
    const fleet = p.fleet || user?.fleet || {};
    setFleetForm({
      name: fleet.name || '',
      business_registration_number: fleet.business_registration_number || '',
      address: fleet.address || '',
      phone: fleet.phone || '',
      email: fleet.email || '',
    });
  }, [profileData, user]);

  const accountMutation = useMutation({
    mutationFn: (data) => authService.updateAccountProfile(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['fleet-profile']);
      const p = res?.profile;
      if (p) setUser(fleetUserFromSessionProfile(p, useAuthStore.getState().user));
      showSuccess('Account profile updated successfully');
    },
    onError: (error) => {
      showError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to update account profile'
      );
    },
  });

  const fleetMutation = useMutation({
    mutationFn: (data) => fleetService.updateProfile(data),
    onSuccess: async () => {
      queryClient.invalidateQueries(['fleet-profile']);
      try {
        const fresh = await authService.getProfile();
        const p = fresh?.profile;
        if (p) setUser(fleetUserFromSessionProfile(p, useAuthStore.getState().user));
      } catch (_) {
        /* ignore */
      }
      showSuccess('Fleet profile updated successfully');
    },
    onError: (error) => {
      showError(
        error.response?.data?.message || error.response?.data?.error || 'Failed to update fleet profile'
      );
    },
  });

  const passwordMutation = useMutation({
    mutationFn: (data) => authService.changePassword(data),
    onSuccess: () => {
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showSuccess('Password changed successfully');
    },
    onError: (error) => {
      showError(
        error.response?.data?.message || error.response?.data?.error || 'Failed to change password'
      );
    },
  });

  const handleAccountSubmit = (e) => {
    e.preventDefault();
    accountMutation.mutate({
      first_name: accountForm.first_name.trim(),
      last_name: accountForm.last_name.trim(),
      phone: accountForm.phone.trim() || undefined,
      preferred_language: accountForm.preferred_language,
    });
  };

  const handleFleetSubmit = (e) => {
    e.preventDefault();
    fleetMutation.mutate(fleetForm);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showError('New passwords do not match');
      return;
    }
    passwordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
  };

  const resetAccount = () => {
    const p = profileData?.profile || {};
    setAccountForm({
      first_name: p.first_name || '',
      last_name: p.last_name || '',
      phone: p.phone || '',
      preferred_language: p.preferred_language || 'en',
    });
  };

  const resetFleet = () => {
    const fleet = profileData?.profile?.fleet || user?.fleet || {};
    setFleetForm({
      name: fleet.name || '',
      business_registration_number: fleet.business_registration_number || '',
      address: fleet.address || '',
      phone: fleet.phone || '',
      email: fleet.email || '',
    });
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-text dark:text-text-dark mb-2">Settings</h1>
        <p className="text-text-secondary dark:text-text-secondary-dark">
          Manage your account, password, and fleet business details
        </p>
      </div>

      {/* Account profile */}
      <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark shadow-lg overflow-hidden">
        <div className="p-4 lg:p-6 border-b border-border dark:border-border-dark bg-gradient-to-r from-surface-secondary/50 to-transparent dark:from-surface-secondary-dark/50">
          <h2 className="text-xl font-bold text-text dark:text-text-dark flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Account profile
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-secondary-dark mt-1">
            Your personal name and contact details (login account)
          </p>
        </div>
        <form onSubmit={handleAccountSubmit} className="p-4 lg:p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-text dark:text-text-dark mb-2">
                First name *
              </label>
              <input
                type="text"
                value={accountForm.first_name}
                onChange={(e) => setAccountForm({ ...accountForm, first_name: e.target.value })}
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text dark:text-text-dark mb-2">
                Last name *
              </label>
              <input
                type="text"
                value={accountForm.last_name}
                onChange={(e) => setAccountForm({ ...accountForm, last_name: e.target.value })}
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-text dark:text-text-dark mb-2">
                <Phone className="w-4 h-4 text-primary" />
                Phone
              </label>
              <input
                type="tel"
                value={accountForm.phone}
                onChange={(e) => setAccountForm({ ...accountForm, phone: e.target.value })}
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text dark:text-text-dark mb-2">
                Preferred language
              </label>
              <select
                value={accountForm.preferred_language}
                onChange={(e) =>
                  setAccountForm({ ...accountForm, preferred_language: e.target.value })
                }
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="en">English</option>
                <option value="no">Norwegian</option>
              </select>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-border dark:border-border-dark">
            <button
              type="button"
              onClick={resetAccount}
              className="px-6 py-3 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark font-medium"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={accountMutation.isPending}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark disabled:opacity-50 font-medium"
            >
              <Save className="w-4 h-4" />
              {accountMutation.isPending ? 'Saving...' : 'Save account'}
            </button>
          </div>
        </form>
      </div>

      {/* Password */}
      <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark shadow-lg overflow-hidden">
        <div className="p-4 lg:p-6 border-b border-border dark:border-border-dark bg-gradient-to-r from-surface-secondary/50 to-transparent dark:from-surface-secondary-dark/50">
          <h2 className="text-xl font-bold text-text dark:text-text-dark flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            Password
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-secondary-dark mt-1">
            Change the password you use to sign in
          </p>
        </div>
        <form onSubmit={handlePasswordSubmit} className="p-4 lg:p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-text dark:text-text-dark mb-2">
                Current password
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                }
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text dark:text-text-dark mb-2">
                New password (min 8 characters)
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text dark:text-text-dark mb-2">
                Confirm new password
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                }
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                required
                minLength={8}
              />
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t border-border dark:border-border-dark">
            <button
              type="submit"
              disabled={passwordMutation.isPending}
              className="px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark disabled:opacity-50 font-medium"
            >
              {passwordMutation.isPending ? 'Updating...' : 'Update password'}
            </button>
          </div>
        </form>
      </div>

      {/* Fleet business */}
      <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark shadow-lg overflow-hidden">
        <div className="p-4 lg:p-6 border-b border-border dark:border-border-dark bg-gradient-to-r from-surface-secondary/50 to-transparent dark:from-surface-secondary-dark/50">
          <h2 className="text-xl font-bold text-text dark:text-text-dark">Fleet profile</h2>
          <p className="text-sm text-text-secondary dark:text-text-secondary-dark mt-1">
            Business information shown to drivers and on documents
          </p>
        </div>

        <form onSubmit={handleFleetSubmit} className="p-4 lg:p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-text dark:text-text-dark mb-2">
                <Building2 className="w-4 h-4 text-primary" />
                Fleet name *
              </label>
              <input
                type="text"
                name="name"
                value={fleetForm.name}
                onChange={(e) => setFleetForm({ ...fleetForm, name: e.target.value })}
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                required
                placeholder="ABC Transport Fleet"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-text dark:text-text-dark mb-2">
                <FileText className="w-4 h-4 text-primary" />
                Business registration number
              </label>
              <input
                type="text"
                name="business_registration_number"
                value={fleetForm.business_registration_number}
                onChange={(e) =>
                  setFleetForm({ ...fleetForm, business_registration_number: e.target.value })
                }
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="REG123456"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-text dark:text-text-dark mb-2">
                <Phone className="w-4 h-4 text-primary" />
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={fleetForm.phone}
                onChange={(e) => setFleetForm({ ...fleetForm, phone: e.target.value })}
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="+1234567890"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-text dark:text-text-dark mb-2">
                <Mail className="w-4 h-4 text-primary" />
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={fleetForm.email}
                onChange={(e) => setFleetForm({ ...fleetForm, email: e.target.value })}
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                required
                placeholder="fleet@example.com"
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-text dark:text-text-dark mb-2">
                <MapPin className="w-4 h-4 text-primary" />
                Address
              </label>
              <textarea
                name="address"
                value={fleetForm.address}
                onChange={(e) => setFleetForm({ ...fleetForm, address: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                placeholder="123 Main St, City, State, ZIP"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-border dark:border-border-dark">
            <button
              type="button"
              onClick={resetFleet}
              className="px-6 py-3 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark font-medium"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={fleetMutation.isPending}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark disabled:opacity-50 font-medium"
            >
              <Save className="w-4 h-4" />
              {fleetMutation.isPending ? 'Saving...' : 'Save fleet profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
